import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { psychologistService } from '../../services/psychologistService';
import { specializationService } from '../../services/specializationService'; 
import SidebarNavigation from '../../components/navigation/SidebarNavigation';
import { Checkbox } from '../../components/ui/Checkbox';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ImageUploadSection from './components/ImageUploadSection';
import { UserCircle } from 'lucide-react';
import { ChevronDown, Check } from 'lucide-react';
import SuccessDialog from './components/SuccessDialog';
import ErrorDialog from './components/ErrorDialog';

const ProfileEditingScreen = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();
  const [daysError, setDaysError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    clinic_name: '',
    full_name: '',
    specialization: '',
    crp: '',
    whatsapp_number: '',
    clinic_address: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [errorSpecialties, setErrorSpecialties] = useState(null);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);

  const sessionDurationOptions = [
    { value: '30', label: '30 minutos' },
    { value: '45', label: '45 minutos' },
    { value: '50', label: '50 minutos' },
    { value: '60', label: '60 minutos' },
    { value: '90', label: '90 minutos' },
  ];

  const weekDays = [
    { id: 'monday', label: 'Segunda' },
    { id: 'tuesday', label: 'Terça' },
    { id: 'wednesday', label: 'Quarta' },
    { id: 'thursday', label: 'Quinta' },
    { id: 'friday', label: 'Sexta' },
    { id: 'saturday', label: 'Sábado' },
    { id: 'sunday', label: 'Domingo' },
  ];

  const [scheduleSettings, setScheduleSettings] = useState({
    sessionDuration: '50',
    workingDays: [],
    startTime: '09:00',
    endTime: '18:00',
    lunchBreak: true,
    lunchStart: '12:00',
    lunchEnd: '13:00',
  });

  useEffect(() => {
    if (!authLoading && userProfile) {
      setFormData({
        clinic_name: userProfile?.clinic_name || '',
        full_name: userProfile?.full_name || '',
        specialization: userProfile?.specialization || '',
        crp: userProfile?.crp || '',
        whatsapp_number: userProfile?.whatsapp_number || '',
        clinic_address: userProfile?.clinic_address || ''
      });

      // Inicializa os dias de trabalho com o valor vindo do banco
      setScheduleSettings(prev => ({
        ...prev,
        workingDays: userProfile?.working_days || []
      }));

      setLoading(false);

    }
  }, [userProfile, authLoading]);

  useEffect(() => {
    if (userProfile) {
      setLogoPreview(userProfile?.clinic_logo_url || null);
      setPhotoPreview(userProfile?.profile_photo_url || null);

      setScheduleSettings(prev => ({
        ...prev,
        workingDays: userProfile.available_days || [],
        startTime: userProfile?.work_begin_time || '09:00',
        endTime:   userProfile?.work_end_time   || '18:00',

        // Intervalo de almoço (JSON)
        lunchBreak: !!userProfile?.work_lunch_interval,    // true se o objeto existir

        lunchStart: userProfile?.work_lunch_interval?.start || '',
        lunchEnd:   userProfile?.work_lunch_interval?.end   || '',      

      }));
  

    }
  }, [userProfile]);


  useEffect(() => {
    async function loadSpecialties() {
      try {
        setLoadingSpecialties(true);
        const { data, error } = await specializationService.getAllSpecializations();
        if (error || !data) {
          console.error("Falha ao carregar especialidades", error);
          setErrorSpecialties("Não foi possível carregar as especialidades");
          return;
        }

        const options = data.map(item => ({
          value: String(item.id),
          label: item.description
        }));

        setSpecialtyOptions(options);
      } catch (err) {
        console.error(err);
        setErrorSpecialties("Erro ao carregar especialidades");
      } finally {
        setLoadingSpecialties(false);
      }
    }

    loadSpecialties();
  }, []);

  useEffect(() => {
    // Só prossegue se tivermos as opções carregadas e o perfil do usuário
    if (!userProfile || !specialtyOptions.length) return;

    const specialization = userProfile.specialization;

    let validSelected = [];

    if (Array.isArray(specialization)) {
      validSelected = specialization
        .map(String)               // garante que todos sejam string
        .map(s => s.trim())
        .filter(Boolean)           // remove valores vazios
        .filter(value => 
          specialtyOptions.some(opt => opt.value === value)
        );
    } else {
      // Caso raro: se por algum motivo não for array, loga o problema
      console.warn("userProfile.specialization não é array:", specialization);
    }

    setSelectedSpecialties(validSelected);
  }, [userProfile, specialtyOptions]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

const handleSpecialtyToggle = (value) => {
  setSelectedSpecialties((prev) => {
    if (prev.includes(value)) {
      return prev.filter((item) => item !== value);
    } else {
      return [...prev, value];
    }
  });
};


  const formatWhatsApp = (value) => {
    const numbers = value?.replace(/\D/g, '');
    if (numbers?.length <= 2) {
      return numbers;
    }
    if (numbers?.startsWith('55')) {
      if (numbers?.length <= 4) {
        return `+${numbers}`;
      }
      if (numbers?.length <= 6) {
        return `+${numbers?.slice(0, 2)} ${numbers?.slice(2)}`;
      }
      if (numbers?.length <= 11) {
        return `+${numbers?.slice(0, 2)} ${numbers?.slice(2, 4)} ${numbers?.slice(4)}`;
      }
      return `+${numbers?.slice(0, 2)} ${numbers?.slice(2, 4)} ${numbers?.slice(4, 9)}-${numbers?.slice(9, 13)}`;
    }
    if (numbers?.length <= 2) {
      return `(${numbers}`;
    }
    if (numbers?.length <= 7) {
      return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2)}`;
    }
    return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2, 7)}-${numbers?.slice(7, 11)}`;
  };

  const formatCRP = (value) => {
    // Remove tudo que não é número
    const numbers = value?.replace(/\D/g, '') || '';

    // CRP tem no máximo 8 dígitos: 2 para região + 6 para sequência
    // Mas aceita até 8 para evitar cortes abruptos
    const cleaned = numbers.slice(0, 8);

    if (cleaned.length <= 2) {
      // Apenas os dois primeiros dígitos (código da região)
      return cleaned;
    } else {
      // Adiciona a barra após os 2 primeiros dígitos
      const region = cleaned.slice(0, 2);
      const sequence = cleaned.slice(2);
      return `${region}/${sequence}`;
    }
  };


  const handleWhatsAppChange = (e) => {
    const formatted = formatWhatsApp(e?.target?.value);
    setFormData((prev) => ({ ...prev, whatsapp_number: formatted }));
  };

  const handleDayToggle = (dayId) => {
    setScheduleSettings((prev) => ({
      ...prev,
      workingDays: prev?.workingDays?.includes(dayId)
        ? prev?.workingDays?.filter((d) => d !== dayId)
        : [...prev?.workingDays, dayId],
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.clinic_name || formData?.clinic_name?.trim()?.length < 3) {
      newErrors.clinic_name = 'Nome da clínica deve ter no mínimo 3 caracteres';
    }

    if (!formData?.full_name || formData?.full_name?.trim()?.length < 3) {
      newErrors.full_name = 'Nome deve ter no mínimo 3 caracteres';
    }

    if (!formData?.specialization) {
      newErrors.specialization = 'Selecione uma especialidade';
    }

    if (formData?.crp && !/^\d{2}\/\d{5,6}$/?.test(formData?.crp)) {
      newErrors.crp = 'CRP inválido. Use o formato: 06/12345';
    }

    if (!formData?.whatsapp_number || formData?.whatsapp_number?.trim()?.length === 0) {
      newErrors.whatsapp_number = 'É obrigatório ter um número de Whatsapp';
    }

    if (formData?.whatsapp_number) {
      const numbers = formData?.whatsapp_number?.replace(/\D/g, '');
      if (numbers?.length < 10 || numbers?.length > 13) {
        newErrors.whatsapp_number = 'Número de WhatsApp inválido';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates = {
        ...formData,                    // mantém os campos que não mudaram
        specialization: selectedSpecialties
          .map(s => s.trim())
          .filter(Boolean),

      available_days: scheduleSettings.workingDays,

      work_begin_time: scheduleSettings.startTime,     // ex: "09:00"
      work_end_time: scheduleSettings.endTime,         // ex: "18:00"

      // Intervalo de almoço como JSON (apenas se ativado)
      work_lunch_interval: scheduleSettings.lunchBreak
        ? {
            start: scheduleSettings.lunchStart,  // ex: "12:00"
            end: scheduleSettings.lunchEnd       // ex: "13:00"
          }
        : null,  // ou {} se preferir enviar objeto vazio
      };      

      if (logoFile) {
        const { url, error: uploadError } = await psychologistService?.uploadClinicLogo(
          userProfile?.id,
          logoFile
        );
        if (uploadError) throw uploadError;
        updates.clinic_logo_url = url;
      }

      if (photoFile) {
        const { url, error: uploadError } = await psychologistService?.uploadProfilePhoto(
          userProfile?.id,
          photoFile
        );
        if (uploadError) throw uploadError;
        updates.profile_photo_url = url;
      }

      const { data, error: updateError } = await psychologistService?.updatePsychologist(
        userProfile?.id,
        updates
      );

      if (updateError) throw updateError;

      navigate('/profile-management');

      setShowSuccessDialog(true);

    } catch (error) {
      console.error('Save error:', error);
      setError(error?.message || 'Erro ao salvar perfil. Tente novamente.');
      setShowErrorDialog(true);

    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Icon name="Loader2" className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#d3d3d3' }}>
      <SidebarNavigation />
      <main className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-4 pt-4 sm:pt-4 lg:pt-6 lg:mt-0 max-w-full overflow-x-hidden">
          {/* Header - Standardized Format */}
          <div className="mb-8">
            <div className="bg-gray-100 rounded-xl shadow-lg shadow-md p-6 max-w-6xl">

              {/* Desktop */}
              <div className="hidden md:flex items-center gap-3 mb-2 flex-nowrap">
                <UserCircle className="w-8 h-8 text-[#969696]" />
                <h1 className="text-3xl font-bold text-[#969696] font-manrope whitespace-nowrap">
                  Editar Perfil
                </h1>
              </div>

              {/* Mobile */}
              <div className="md:hidden flex items-center gap-2 mb-1">
                <UserCircle className="w-8 h-8 text-[#969696]" />
                
                <h1 className="text-2xl font-bold text-[#969696] font-manrope">
                  Editar Perfil
                </h1>
              </div>

              {/* Desktop - Mostra subtítulo */}
              <p className="hidden md:block text-[#969696] font-manrope text-lg">
                Atualize os seus dados
              </p>
            </div>
          </div>


          {error && (
            <div className="mb-6 bg-error/10 border border-error rounded-lg p-4">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building2" size={20} color="var(--color-primary)" />
                Informações da Clínica
              </h3>

              <div className="space-y-4">
                <Input
                  label="Nome da Clínica"
                  type="text"
                  name="clinic_name"
                  placeholder="Clínica de Psicologia"
                  value={formData?.clinic_name}
                  onChange={handleInputChange}
                  error={errors?.clinic_name}
                  required
                />

                <Input
                  label="Nome de Exibição (Psicólogo)"
                  type="text"
                  name="full_name"
                  placeholder="Como você quer ser chamado"
                  description="Este nome aparecerá na página pública"
                  value={formData?.full_name}
                  onChange={handleInputChange}
                  error={errors?.full_name}
                  required
                />

                <ImageUploadSection
                  logoPreview={logoPreview}
                  photoPreview={photoPreview}
                  onLogoChange={(file, preview) => {
                    setLogoFile(file);
                    setLogoPreview(preview);
                  }}
                  onPhotoChange={(file, preview) => {
                    setPhotoFile(file);
                    setPhotoPreview(preview);
                  }}
                  errors={errors}
                />
              </div>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={20} color="var(--color-primary)" />
                Informações Profissionais
              </h3>

              {/* Especialidades */}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Especialidades <span className="text-error">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Você pode selecionar múltiplas especialidades
                  </p>

                  {/* Multi-Select Custom - Substituindo o Select antigo */}
                  <div className="relative">
                    {/* Botão principal do dropdown */}
                    <button
                      type="button"
                      onClick={() => setOpen(!open)}
                      className={`flex items-center justify-between w-full p-3 rounded-lg border-2 transition-all duration-200 ${
                        selectedSpecialties.length > 0
                          ? 'border-primary bg-primary/5'
                          : errors?.specialization
                          ? 'border-error bg-error/5'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <span className="text-sm text-foreground">
                        {selectedSpecialties.length === 0
                          ? 'Selecione suas especialidades'
                          : `${selectedSpecialties.length} especialidade(s) selecionada(s)`}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-foreground transition-transform duration-200 ${
                          open ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Mensagem de erro (se houver) */}
                    {errors?.specialization && (
                      <p className="text-sm text-error mt-2">{errors.specialization}</p>
                    )}

                    {/* Dropdown com opções */}
                    {open && (
                      <>
                        <div className="absolute z-10 mt-2 w-full overflow-auto rounded-lg border-2 border-border bg-card shadow-lg max-h-60">
                          {specialtyOptions?.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleSpecialtyToggle(option.value)}
                              className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-primary/10 transition-colors text-left"
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                  selectedSpecialties.includes(option.value)
                                    ? 'border-primary bg-primary'
                                    : 'border-border bg-transparent'
                                }`}
                              >
                                {selectedSpecialties.includes(option.value) && (
                                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                                )}
                              </div>
                              <span className="text-sm text-foreground">{option.label}</span>
                            </button>
                          ))}
                        </div>

                        {/* Overlay para fechar ao clicar fora */}
                        <div
                          className="fixed inset-0 z-0"
                          onClick={() => setOpen(false)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>


              <div className="space-y-4 mt-4">
                <Input
                  label="CRP (Conselho Regional de Psicologia)"
                  type="text"
                  name="crp"
                  placeholder="06/12345"
                  description="Formato: UF/Número (ex: 06/12345)"
                  value={formData?.crp}
                  onChange={(e) => {
                    const formatted = formatCRP(e.target.value);
                    handleInputChange({ target: { name: 'crp', value: formatted } });
                  }}                  
                  error={errors?.crp}
                  required
                />

                <Input
                  label="Número de WhatsApp"
                  type="tel"
                  name="whatsapp_number"
                  placeholder="+55 11 98765-4321 ou (11) 98765-4321"
                  description="Aceita formato internacional (+55) ou local"
                  value={formData?.whatsapp_number}
                  onChange={handleWhatsAppChange}
                  error={errors?.whatsapp_number}
                  required
                />

                { false &&
                <Input
                  label="Endereço da Clínica (Opcional)"
                  type="text"
                  name="clinic_address"
                  placeholder="Rua Exemplo, 123 - Bairro, Cidade - UF"
                  value={formData?.clinic_address}
                  onChange={handleInputChange}
                /> }

              </div>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-5 flex items-center gap-2">
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
                Disponibilidade
              </h3>

              {/* TODO: Disponibilidade  */}

              <div className="space-y-6">
                <Select
                  label="Duração Padrão das Sessões"
                  options={sessionDurationOptions}
                  value={scheduleSettings?.sessionDuration}
                  onChange={(value) =>
                    setScheduleSettings((prev) => ({ ...prev, sessionDuration: value }))
                  }
                  description="Você poderá ajustar individualmente para cada paciente"
                />

                <div data-days-section>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Dias de Atendimento
                  </label>

                  {/* Mensagem de erro */}
                  {daysError && (
                    <p className="text-sm text-destructive mb-3 font-medium">
                      Selecione pelo menos um dia da semana
                    </p>
                  )}

                  <div 
                    className={`grid grid-cols-2 sm:grid-cols-4 gap-2 p-1 rounded-lg transition-all ${
                      daysError ? 'bg-destructive/10 border border-destructive/40' : ''
                    }`}
                  >
                    
                  {weekDays.map((day) => (
                    <div
                      key={day.id}
                      onClick={() => {
                        handleDayToggle(day.id);
                        // Limpa erro ao selecionar qualquer dia
                        if (daysError) setDaysError(false);
                      }}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                        scheduleSettings.workingDays.includes(day.id)
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-border bg-muted/50 text-muted-foreground hover:border-primary/50 hover:bg-muted'
                      } ${daysError && !scheduleSettings.workingDays.includes(day.id) ? 'border-destructive/40' : ''}`}
                    >
                      <p className="text-sm">{day.label}</p>
                    </div>
                  ))}
                  
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Horário de Início
                    </label>
                    <input
                      type="time"
                      value={scheduleSettings?.startTime}
                      onChange={(e) =>
                        setScheduleSettings((prev) => ({
                          ...prev,
                          startTime: e?.target?.value,
                        }))
                      }
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Horário de Término
                    </label>
                    <input
                      type="time"
                      value={scheduleSettings?.endTime}
                      onChange={(e) =>
                        setScheduleSettings((prev) => ({
                          ...prev,
                          endTime: e?.target?.value,
                        }))
                      }
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <Checkbox
                  label="Incluir intervalo para almoço"
                  checked={scheduleSettings?.lunchBreak}
                  onChange={(e) =>
                    setScheduleSettings((prev) => ({
                      ...prev,
                      lunchBreak: e?.target?.checked,
                    }))
                  }
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Início do Almoço
                    </label>
                    <input
                      type="time"
                      value={scheduleSettings?.lunchStart}
                      onChange={(e) =>
                        setScheduleSettings((prev) => ({
                          ...prev,
                          lunchStart: e?.target?.value,
                        }))
                      }
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Fim do Almoço
                    </label>
                    <input
                      type="time"
                      value={scheduleSettings?.lunchEnd}
                      onChange={(e) =>
                        setScheduleSettings((prev) => ({
                          ...prev,
                          lunchEnd: e?.target?.value,
                        }))
                      }
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                </div>
              </div>
            </div>
            {/* Fim Disponibilidade */}


            <div className="flex justify-end gap-4 pt-4">
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                iconName={saving ? 'Loader2' : 'Check'}
                iconPosition="left"
              >
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        </div>    
      </main>

      {/* Success Dialog */}
      <SuccessDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message="Perfil gravado com sucesso"
      />
      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message="Falha ao tentar gravar perfil"
      />

    </div>
  );
};

export default ProfileEditingScreen;